import { useI18n } from "@/i18n/I18nProvider";

/**
 * ExploreItly — placed between HeroTurista and ItalyMap.
 * Communicates three P0 features missing from the site:
 *  (1) tripartite category distinction (bookable / showcase / free)
 *  (2) automatic location detection
 *  (3) events-in-your-area coverage
 *  Plus a "trust strip" (P1): confirmation notifications, offline QR/booking, Report button.
 */
function CategoryGrid({ t, categories }) {
    return (
        <div
            className="itly-explore-grid"
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 20,
                marginBottom: 56,
            }}
        >
            {categories.map((c) => (
                <article
                    key={c.key}
                    data-testid={`explore-${c.key}`}
                    style={{
                        background: "var(--itly-surface)",
                        borderRadius: 18,
                        padding: "28px 26px",
                        border: "1px solid var(--itly-border)",
                        boxShadow: "var(--itly-card-shadow)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                    }}
                >
                    <div
                        aria-hidden="true"
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: c.iconBg,
                            color: "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "Fraunces, serif",
                            fontSize: 20,
                            fontWeight: 600,
                        }}
                    >
                        {c.iconGlyph}
                    </div>
                    <div
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 11.5,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            color: c.accent,
                            fontWeight: 600,
                        }}
                    >
                        {t(`explore.${c.key}.badge`)}
                    </div>
                    <h3
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontSize: 22,
                            lineHeight: 1.2,
                            color: "var(--itly-fg-strong)",
                            margin: 0,
                        }}
                    >
                        {t(`explore.${c.key}.title`)}
                    </h3>
                    <p
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14.5,
                            lineHeight: 1.55,
                            color: "var(--itly-muted)",
                            margin: 0,
                        }}
                    >
                        {t(`explore.${c.key}.body`)}
                    </p>
                </article>
            ))}
        </div>
    );
}

function EventsGeoRow({ t }) {
    return (
        <div
            className="itly-explore-secondary"
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 20,
                marginBottom: 56,
            }}
        >
            <article
                data-testid="explore-events"
                style={{
                    background: "var(--itly-peach-050)",
                    borderRadius: 18,
                    padding: "22px 24px",
                    border: "1px solid rgba(232, 113, 76, 0.18)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 16,
                }}
            >
                <span
                    aria-hidden="true"
                    style={{
                        fontSize: 24,
                        lineHeight: 1,
                        color: "var(--itly-orange-500)",
                        flexShrink: 0,
                    }}
                >
                    ✦
                </span>
                <div>
                    <h3
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontSize: 20,
                            lineHeight: 1.25,
                            color: "var(--itly-fg-strong)",
                            margin: 0,
                            marginBottom: 6,
                        }}
                    >
                        {t("explore.events.title")}
                    </h3>
                    <p
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14,
                            lineHeight: 1.55,
                            color: "var(--itly-muted)",
                            margin: 0,
                        }}
                    >
                        {t("explore.events.body")}
                    </p>
                </div>
            </article>
            <article
                data-testid="explore-geo"
                style={{
                    background: "var(--itly-teal-050)",
                    borderRadius: 18,
                    padding: "22px 24px",
                    border: "1px solid rgba(15, 61, 62, 0.14)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 16,
                }}
            >
                <span
                    aria-hidden="true"
                    style={{
                        fontSize: 22,
                        lineHeight: 1,
                        color: "var(--itly-fg-strong)",
                        flexShrink: 0,
                    }}
                >
                    ◎
                </span>
                <div>
                    <h3
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontSize: 20,
                            lineHeight: 1.25,
                            color: "var(--itly-fg-strong)",
                            margin: 0,
                            marginBottom: 6,
                        }}
                    >
                        {t("explore.geo.title")}
                    </h3>
                    <p
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 14,
                            lineHeight: 1.55,
                            color: "var(--itly-muted)",
                            margin: 0,
                        }}
                    >
                        {t("explore.geo.body")}
                    </p>
                </div>
            </article>
        </div>
    );
}

function TrustStrip({ t, trustChips }) {
    return (
        <div
            data-testid="explore-trust"
            style={{
                background: "var(--itly-surface)",
                border: "1px solid var(--itly-border)",
                borderRadius: 20,
                padding: "26px 28px",
                boxShadow: "var(--itly-card-shadow)",
            }}
        >
            <h3
                style={{
                    fontFamily: "Fraunces, serif",
                    fontSize: 20,
                    color: "var(--itly-fg-strong)",
                    margin: 0,
                    marginBottom: 20,
                    letterSpacing: "-0.005em",
                }}
            >
                {t("explore.trust.title")}
            </h3>
            <div
                className="itly-trust-grid"
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 18,
                }}
            >
                {trustChips.map((chip) => (
                    <div
                        key={chip.key}
                        data-testid={`trust-${chip.key}`}
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 12,
                        }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                width: 34,
                                height: 34,
                                borderRadius: 10,
                                background: "var(--itly-cream)",
                                color: "var(--itly-orange-500)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontFamily: "Fraunces, serif",
                                fontSize: 17,
                                fontWeight: 600,
                                flexShrink: 0,
                                border: "1px solid var(--itly-border)",
                            }}
                        >
                            {chip.glyph}
                        </span>
                        <div>
                            <div
                                style={{
                                    fontFamily: "Inter, sans-serif",
                                    fontSize: 14.5,
                                    fontWeight: 600,
                                    color: "var(--itly-fg-strong)",
                                    marginBottom: 3,
                                }}
                            >
                                {t(`explore.trust.${chip.key}`)}
                            </div>
                            <div
                                style={{
                                    fontFamily: "Inter, sans-serif",
                                    fontSize: 13,
                                    lineHeight: 1.5,
                                    color: "var(--itly-muted)",
                                }}
                            >
                                {t(`explore.trust.${chip.key}Body`)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function ExploreItly() {
    const { t } = useI18n();
    const categories = [
        {
            key: "cat1",
            iconBg: "linear-gradient(135deg, #E8714C 0%, #D45B3A 100%)",
            iconGlyph: "→",
            accent: "var(--itly-orange-500)",
        },
        {
            key: "cat2",
            iconBg: "linear-gradient(135deg, #17696C 0%, #0F3D3E 100%)",
            iconGlyph: "✧",
            accent: "var(--itly-fg-strong)",
        },
        {
            key: "cat3",
            iconBg: "linear-gradient(135deg, #78A88F 0%, #4E7F65 100%)",
            iconGlyph: "▲",
            accent: "#4E7F65",
        },
    ];
    const trustChips = [
        { key: "notif", glyph: "◉" },
        { key: "report", glyph: "!" },
    ];

    return (
        <section
            id="esplora"
            data-testid="explore-itly"
            data-reveal="up"
            style={{
                padding: "96px 32px 88px",
                background: "var(--itly-page)",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div style={{ maxWidth: 1160, margin: "0 auto" }}>
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: 56 }}>
                    <div className="itly-badge" data-testid="explore-badge">
                        {t("explore.badge")}
                    </div>
                    <h2
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontSize: "clamp(32px, 4vw, 52px)",
                            lineHeight: 1.05,
                            color: "var(--itly-fg-strong)",
                            marginTop: 20,
                            marginBottom: 18,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {t("explore.title_a")}
                        <br />
                        <em style={{ color: "var(--itly-orange-500)", fontStyle: "italic" }}>
                            {t("explore.title_b")}
                        </em>
                    </h2>
                    <p
                        style={{
                            maxWidth: 720,
                            margin: "0 auto",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: 1.65,
                            color: "var(--itly-muted)",
                        }}
                    >
                        {t("explore.sub")}
                    </p>
                </div>

                {/* Tripartite category grid */}
                <CategoryGrid t={t} categories={categories} />

                {/* Events + Geolocation row */}
                <EventsGeoRow t={t} />

                {/* Trust strip: notifiche + segnala */}
                <TrustStrip t={t} trustChips={trustChips} />
            </div>
        </section>
    );
}
